import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore, useIsOverQuota } from '@qse/stores'
import type { QueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { toast } from 'sonner'

import type { FolderInit } from './useFolderUpload.helpers'
import { initiateFolderTreeWithTracking } from './useFolderUploadWithTracking'

// Utility function to check if a file should be filtered out (hidden or system files)
const shouldFilterFile = (fileName: string): boolean => {
  const isHiddenFile = fileName.startsWith('.')
  const isSystemFile = fileName === 'Thumbs.db' || fileName === 'desktop.ini'

  return isHiddenFile || isSystemFile
}

export interface UseFolderUploadArgs {
  pathname: string
  currentFolderId?: string
  queryClient: QueryClient
  addFiles: (
    files: (File & { path?: string })[],
    folderId: string | undefined,
    _unused: undefined,
    qc: QueryClient,
    parentPath: string,
    folderInit?: FolderInit, // 👈 مهم
  ) => void
}

export type UploadEntry =
  | { kind: 'dir'; path: string; name?: string }
  | { kind: 'file'; path: string; file: File }

declare global {
  interface Window {
    showDirectoryPicker?: (options?: { mode?: 'read' }) => Promise<FileSystemDirectoryHandle>
  }
}

/** Walks through the selected directory and collects files + empty folders */
async function collectEntriesFromHandle(
  dir: FileSystemDirectoryHandle,
  basePath = '',
): Promise<UploadEntry[]> {
  const out: UploadEntry[] = []

  // Use entries() which yields [name, handle]
  for await (const [name, handle] of (dir as any).entries() as AsyncIterable<
    [string, FileSystemHandle]
  >) {
    const childPath = basePath ? `${basePath}/${name}` : name

    if (handle.kind === 'directory') {
      out.push({ kind: 'dir', path: childPath + '/', name })
      const nested = await collectEntriesFromHandle(handle as FileSystemDirectoryHandle, childPath)

      out.push(...nested)
    } else {
      const file = await (handle as FileSystemFileHandle).getFile()

      // Filter hidden files (files starting with .) and system files - they're not visible to users
      if (!shouldFilterFile(file.name)) {
        out.push({ kind: 'file', path: childPath, file })
      }
    }
  }

  return out
}

function dedupeEntries(entries: UploadEntry[]): UploadEntry[] {
  const seen = new Set<string>()
  const result: UploadEntry[] = []

  for (const e of entries) {
    const key = e.kind === 'dir' ? `D:${e.path}` : `F:${e.path}`

    if (!seen.has(key)) {
      seen.add(key)
      result.push(e)
    }
  }

  return result
}

// Collects everything + adds the selected directory itself as a dir first
async function collectWithRoot(dir: FileSystemDirectoryHandle): Promise<UploadEntry[]> {
  const rootPath = dir.name + '/'
  const rootDir: UploadEntry = { kind: 'dir', path: rootPath, name: dir.name }
  const children = await collectEntriesFromHandle(dir, dir.name)

  return dedupeEntries([rootDir, ...children])
}

/** Converts .DS_Store files to parent folder representation in logs (optional) */
function normalizeEntriesForLogging(entries: UploadEntry[], rootName: string) {
  return entries.map(e => {
    if (e.kind === 'file') {
      const fileAny = e as any
      const fileName: string = fileAny.file?.name || e.path.split('/').pop() || ''

      if (fileName === '.DS_Store') {
        let parentPath = e.path.replace(/\/?\.DS_Store$/, '/')

        if (parentPath === '/' || parentPath === '') {
          parentPath = rootName ? `${rootName}/` : '/'
        }
        const folderName = parentPath.endsWith('/')
          ? parentPath.slice(0, -1).split('/').pop() || rootName || ''
          : parentPath.split('/').pop() || rootName || ''

        return {
          kind: 'file' as const,
          path: parentPath,
          name: folderName,
          sizeBytes: fileAny.file?.size ?? 0,
          type: fileAny.file?.type || 'n/a',
        }
      }

      return {
        kind: 'file' as const,
        path: e.path,
        sizeBytes: fileAny.file?.size ?? 0,
        type: fileAny.file?.type || 'n/a',
      }
    }

    return e
  })
}

/** ------------------------------------------------------------------------------ */

export function useFolderUpload({
  pathname,
  currentFolderId,
  queryClient,
  addFiles,
}: UseFolderUploadArgs) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isOverQuota = useIsOverQuota()
  const handleFileUpload = useCallback(() => {
    if (isOverQuota) {
      return
    }
    if (!fileInputRef.current) return
    ;(fileInputRef.current as any).webkitdirectory = false
    fileInputRef.current.multiple = true
    fileInputRef.current.click()
  }, [isOverQuota])

  const handleFolderCreation = useCallback(() => {
    useActiveModelStore.setState({
      data: {
        parentId: currentFolderId || '',
        parentPath: pathname.startsWith('/folders/') ? pathname : '/',
        onSuccess: (_folderName: string) => {},
        modalHeader: {
          title: 'Create Folder',
          showCloseButton: true,
        },
      },
      width: 'max-w-[490px]',
      modelActive: ModelControlEnum.CREATE_FOLDER,
      toggleModel: true,
      scrollBehavior: 'inside',
    })
  }, [currentFolderId, pathname])

  // Preferred path for folder uploads: FS Access API
  const handleFolderUpload = useCallback(async () => {
    if (isOverQuota) {
      return
    }
    // Try modern File System Access API first (supports empty folders)
    if (window.showDirectoryPicker) {
      try {
        const dirHandle = await window.showDirectoryPicker({ mode: 'read' })
        const entries = await collectWithRoot(dirHandle)

        // Separate dirs and files
        const dirs = entries.filter(e => e.kind === 'dir')
        const fileEntries = entries.filter(e => e.kind === 'file')
        const files = fileEntries
          .map(e => {
            if (e.kind === 'file') {
              const fileWithPath = e.file as File & { path?: string }

              fileWithPath.path = e.path

              return fileWithPath
            }

            return null
          })
          .filter(Boolean) as (File & { path?: string })[]

        // Build tree structure from dirs + files
        const rootName = dirHandle.name

        type DirNode = { name: string; children?: DirNode[] }
        const rootNode: DirNode = { name: rootName, children: [] }
        const pathToNode = new Map<string, DirNode>([[rootName, rootNode]])

        const ensureNode = (relPath: string) => {
          const parts = relPath.split('/').filter(Boolean)
          let curPath = ''
          let parent = rootNode

          for (const part of parts) {
            curPath = curPath ? `${curPath}/${part}` : part
            let node = pathToNode.get(curPath)

            if (!node) {
              node = { name: part, children: [] }
              parent.children = parent.children || []
              parent.children.push(node)
              pathToNode.set(curPath, node)
            }
            parent = node
          }

          return parent
        }

        // Build tree from directories
        for (const dir of dirs) {
          const path = dir.path.replace(/\/$/, '') // remove trailing slash

          if (path && path !== rootName) {
            ensureNode(path)
          }
        }

        // Build tree from file paths
        for (const f of files) {
          const rel = (f.path || '').replace(/\/[^/]*$/, '') // folder path only

          if (rel) {
            ensureNode(rel)
          }
        }

        const picked = { rootName, files, tree: rootNode }
        const parentPath = pathname.startsWith('/folders/') ? pathname : '/'

        // Show confirmation dialog
        const totalFiles = files.length
        const folderName = rootName

        if (totalFiles > 0) {
          const confirmed = window.confirm(
            `Upload ${totalFiles} files to this site?\n\nThis will upload all files from "${folderName}". Only do this if you trust the site.`,
          )

          if (!confirmed) {
            return
          }
        }

        try {
          const { result } = await initiateFolderTreeWithTracking({
            currentFolderId,
            parentPath,
            picked,
            queryClient,
          })

          if (!result) {
            return
          }

          // Check if quota was exceeded (special return value)
          if ('quotaExceeded' in result && result.quotaExceeded) {
            return
          }

          // Upload files if any exist - at this point res must be the success case
          if ('init' in result && 'files' in result) {
            addFiles(result.files, currentFolderId, undefined, queryClient, parentPath, result.init)
          }
        } catch (e) {
          console.error('Folder upload failed:', e)
          toast.error('Folder upload failed.')
        }

        return
      } catch (err) {
        // User cancelled or error - fall through to old method
        const error = err as Error & { name?: string }

        if (error.name !== 'AbortError') {
          console.warn('File System Access API failed, falling back to webkitdirectory:', error)
        } else {
          // User cancelled - don't show error or fallback
          return
        }
      }
    }

    // --- Fallback: <input webkitdirectory> (cannot see empty folders) ---
    if (!fileInputRef.current) return
    ;(fileInputRef.current as any).webkitdirectory = true
    fileInputRef.current.multiple = true
    fileInputRef.current.click()
  }, [pathname, currentFolderId, queryClient, addFiles, isOverQuota])

  // Handles both regular file input and (fallback) folder input
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement & { webkitdirectory?: boolean }
      const files = Array.from(input.files || []) as (File & { path?: string })[]

      // always reset the input at the end
      const resetInput = () => {
        if (fileInputRef.current) fileInputRef.current.value = ''
      }

      if (!files.length) return resetInput()

      const parentPath = pathname.startsWith('/folders/') ? pathname : '/'
      // Check if this is a directory upload: webkitdirectory flag OR webkitRelativePath contains a path separator
      // For regular file uploads, webkitRelativePath is empty or just the filename without any path
      const isDirectoryUpload =
        !!input.webkitdirectory || (files[0].webkitRelativePath || '').includes('/')

      // filter hidden/system files (your rule)
      const filtered = files.filter(f => !shouldFilterFile(f.name))

      if (!filtered.length) return resetInput()

      if (isDirectoryUpload) {
        // derive root folder name and a hierarchical tree from webkitRelativePath
        const firstRel = (filtered[0].webkitRelativePath || '').split('/')[0]
        const rootName = firstRel || 'Untitled Folder'

        // Build tree: { name, children[] } from file paths
        type DirNode = { name: string; children?: DirNode[] }
        const rootNode: DirNode = { name: rootName, children: [] }
        const pathToNode = new Map<string, DirNode>([[rootName, rootNode]])

        const ensureNode = (relPath: string) => {
          const parts = relPath.split('/').filter(Boolean)
          let curPath = ''
          let parent = rootNode

          for (const part of parts) {
            curPath = curPath ? `${curPath}/${part}` : part
            let node = pathToNode.get(curPath)

            if (!node) {
              node = { name: part, children: [] }
              parent.children = parent.children || []
              parent.children.push(node)
              pathToNode.set(curPath, node)
            }
            parent = node
          }

          return parent
        }

        // Add all intermediary folders from each file’s path
        for (const f of filtered) {
          const rel = (f.webkitRelativePath || '').replace(/\/[^/]*$/, '') // folder path only

          if (rel)
            ensureNode(rel)
            // attach a stable path for backend (same as you did before)
          ;(f as any).path = f.webkitRelativePath || f.name
        }

        // Build the PickedResult expected by initiateFolderTree
        const picked = {
          rootName,
          files: filtered as (File & { path?: string })[],
          tree: rootNode,
        }

        // Show confirmation dialog
        const totalFiles = filtered.length
        const folderName = rootName

        if (totalFiles > 0) {
          const confirmed = window.confirm(
            `Upload ${totalFiles} files to this site?\n\nThis will upload all files from "${folderName}". Only do this if you trust the site.`,
          )

          if (!confirmed) {
            return resetInput()
          }
        }

        try {
          // Initiate folders first, get init + normalized files list
          const { result } = await initiateFolderTreeWithTracking({
            currentFolderId,
            parentPath,
            picked,
            queryClient,
          })

          if (!result) {
            return resetInput()
          }

          // Check if quota was exceeded (special return value)
          if ('quotaExceeded' in result && result.quotaExceeded) {
            return resetInput()
          }

          // Optional: one informational toast (not a browser confirm)
          // toast.info(`Uploading ${res.files.length} files from "${rootName}"...`)

          // Upload with folder init - at this point res must be the success case
          if ('init' in result && 'files' in result) {
            addFiles(result.files, currentFolderId, undefined, queryClient, parentPath, result.init)
          }
        } catch (e) {
          console.error('Folder upload failed:', e)
          toast.error('Folder upload failed.')
        } finally {
          resetInput()
        }

        return
      }

      // Regular file upload (not a folder)
      try {
        addFiles(filtered, currentFolderId, undefined, queryClient, parentPath)
      } finally {
        resetInput()
      }
    },
    [pathname, currentFolderId, queryClient, addFiles],
  )

  return {
    fileInputRef,
    handleFileUpload,
    handleFolderUpload,
    handleFileChange,
    handleFolderCreation,
    // exposing utils just in case you want them elsewhere:
    collectEntriesFromHandle,
    collectWithRoot,
    dedupeEntries,
    normalizeEntriesForLogging,
  }
}
